import { create } from 'zustand';
import { i18n, SUPPORTED_LANGUAGES, type Language } from '@tarifdata/i18n';

type Theme = 'light' | 'dark';

function initTheme(): Theme {
  const stored = localStorage.getItem('md-theme');
  if (stored === 'dark' || stored === 'light') return stored;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function initLanguage(): Language {
  const stored = localStorage.getItem('language') as Language | null;
  if (stored && SUPPORTED_LANGUAGES.some((l) => l.code === stored)) return stored;
  const browserLang = navigator.language?.split('-')[0];
  if (browserLang && SUPPORTED_LANGUAGES.some((l) => l.code === browserLang))
    return browserLang as Language;
  return 'en';
}

interface SettingsState {
  preferredCurrency: string;
  notificationsEnabled: boolean;
  emailNotifications: boolean;
  telegramNotifications: boolean;
  showAddExchangeModal: boolean;
  theme: Theme;
  language: Language;

  setPreferredCurrency: (currency: string) => void;
  setNotificationsEnabled: (enabled: boolean) => void;
  setEmailNotifications: (enabled: boolean) => void;
  setTelegramNotifications: (enabled: boolean) => void;
  setShowAddExchangeModal: (show: boolean) => void;
  setTheme: (theme: Theme) => void;
  setLanguage: (lang: Language) => void;
}

export const useSettingsStore = create<SettingsState>((set) => ({
  preferredCurrency: 'USD',
  notificationsEnabled: true,
  emailNotifications: false,
  telegramNotifications: true,
  showAddExchangeModal: false,
  theme: initTheme(),
  language: initLanguage(),

  setPreferredCurrency: (currency) => set({ preferredCurrency: currency }),
  setNotificationsEnabled: (enabled) => set({ notificationsEnabled: enabled }),
  setEmailNotifications: (enabled) => set({ emailNotifications: enabled }),
  setTelegramNotifications: (enabled) => set({ telegramNotifications: enabled }),
  setShowAddExchangeModal: (show) => set({ showAddExchangeModal: show }),
  setTheme: (theme) => {
    localStorage.setItem('md-theme', theme);
    set({ theme });
  },
  setLanguage: (lang) => {
    localStorage.setItem('language', lang);
    i18n.changeLanguage(lang);
    set({ language: lang });
  },
}));
